import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { parseWithZod, getZodConstraint } from '@conform-to/zod/v4'
import { useEffect, useState } from 'react'
import { data, Form, Link, useLoaderData } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import {
	Field,
	TextareaField,
	ErrorList,
	SelectField,
	MultiSelectField,
} from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useSupabaseUpload } from '#app/hooks/use-supabase-upload.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server'
import { useIsPending } from '#app/utils/misc.tsx'
import { getRentalDraft } from '#app/utils/rental-session.server.ts'
import { type Route } from './+types/index.ts'
import { submitRental } from './__submit-rental.server.tsx'
import { getSubmitRentalSchema } from './submit-schema.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	let user: { id: string; name: string | null; email: string } | null = null
	if (userId) {
		user = await prisma.user.findUnique({
			where: { id: userId },
			select: { id: true, name: true, email: true },
		})
	}

	// Get rental draft from session (for users who registered after starting submission)
	const rentalDraft = await getRentalDraft(request)

	// Get all categories and cities for the form
	const [categories, cities] = await Promise.all([
		prisma.category.findMany({
			select: {
				id: true,
				name: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.city.findMany({
			select: {
				id: true,
				name: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
	])

	return data({ user, categories, cities, rentalDraft })
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const intent = formData.get('intent')

	if (intent === 'submit-rental') {
		return submitRental({ request, formData })
	}

	return data({ error: 'Invalid intent' }, { status: 400 })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Submit Rental', url: '/submit-rental' }
	},
	seo({ canonical }: { canonical: string }): SeoProps {
		return {
			title: 'Submit Your Rental Listing - RentDFW.com',
			description:
				"Submit your rental listing to RentDFW.com. We'll review your submission and get back to you as soon as possible.",
			canonical,
			openGraph: {
				title: 'Submit Your Rental Listing - RentDFW.com',
				description:
					"Submit your rental listing to RentDFW.com. We'll review your submission and get back to you as soon as possible.",
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Submit Your Rental Listing - RentDFW.com',
				description:
					"Submit your rental listing to RentDFW.com. We'll review your submission and get back to you as soon as possible.",
			},
		}
	},
}

export default function SubmitRentalRoute() {
	const { user, categories, cities, rentalDraft } =
		useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const schema = getSubmitRentalSchema({ isLoggedIn: Boolean(user) })

	const [form, fields] = useForm({
		id: 'submit-rental-form',
		constraint: getZodConstraint(schema),
		defaultValue: rentalDraft
			? {
					title: rentalDraft.title,
					categoryId: rentalDraft.categoryId,
					cityIds: rentalDraft.cityIds,
					description: rentalDraft.description,
					dailyRate: rentalDraft.dailyRate?.toString(),
					hourlyRate: rentalDraft.hourlyRate?.toString(),
					contactName: rentalDraft.contactName,
					contactEmail: rentalDraft.contactEmail,
				}
			: undefined,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema })
		},
		shouldRevalidate: 'onBlur',
	})

	// Upload handling - only for authenticated users
	const [uploadedMediaId, setUploadedMediaId] = useState<string>('')
	const uploadPath = user ? `users/${user.id}/rentals` : 'rentals'
	const {
		files,
		setFiles,
		loading: uploading,
		errors: uploadErrors,
		uploadedMedia,
		setUploadedMedia,
		getRootProps,
		getInputProps: getDropzoneInputProps,
		isDragActive,
		open,
		onUpload,
	} = useSupabaseUpload({
		bucketName: 'public-assets',
		path: uploadPath,
		allowedMimeTypes: ['image/*'],
		maxFileSize: 1024 * 1024 * 5, // 5MB
		maxFiles: 1,
	})

	// Update form when media is uploaded
	useEffect(() => {
		if (uploadedMedia.length > 0) {
			setUploadedMediaId(uploadedMedia[0]!.id)
		}
	}, [uploadedMedia])

	const hasUploadedImage = uploadedMedia.length > 0 || uploadedMediaId

	return (
		<div className="container mx-auto max-w-2xl pt-12 pb-24">
			<Card>
				<CardHeader>
					<CardTitle>Submit your rental listing</CardTitle>
				</CardHeader>
				<CardContent>
					<Form method="post" {...getFormProps(form)}>
						<HoneypotInputs />
						<input type="hidden" name="intent" value="submit-rental" />
						<input
							type="hidden"
							name="featuredMediaId"
							id={fields.featuredMediaId?.id}
							aria-invalid={
								fields.featuredMediaId?.errors?.length ? true : undefined
							}
							aria-describedby={
								fields.featuredMediaId?.errors?.length
									? `${fields.featuredMediaId.id}-error`
									: undefined
							}
							value={uploadedMediaId}
						/>

						<Field
							labelProps={{ children: 'Title' }}
							inputProps={{
								...getInputProps(fields.title!, { type: 'text' }),
								placeholder: 'Party bounce house with slide',
							}}
							errors={fields.title?.errors}
						/>

						{categories.length > 0 && (
							<SelectField
								labelProps={{ children: 'Category' }}
								selectProps={{
									...getInputProps(fields.categoryId!, { type: 'text' }),
									placeholder: 'Select category...',
								}}
								selectOptions={categories.map((category) => ({
									value: category.id,
									label: category.name,
								}))}
								field={fields.categoryId!}
								errors={fields.categoryId?.errors}
							/>
						)}

						{cities.length > 0 && (
							<MultiSelectField
								className="mb-6"
								labelProps={{ children: 'Cities' }}
								multiSelectProps={{
									name: fields.cityIds!.name,
									placeholder: 'Select cities...',
									emptyText: 'No cities found',
								}}
								multiSelectOptions={cities.map((city) => ({
									value: city.id,
									label: city.name,
								}))}
								field={fields.cityIds!}
								errors={fields.cityIds?.errors}
							/>
						)}

						<div className="grid grid-cols-2 gap-4">
							<Field
								labelProps={{ children: 'Daily Rate' }}
								inputProps={{
									...getInputProps(fields.dailyRate!, { type: 'number' }),
									placeholder: '50.00',
									step: '0.01',
									min: '0',
								}}
								errors={fields.dailyRate?.errors}
							/>

							<Field
								labelProps={{ children: 'Hourly Rate' }}
								inputProps={{
									...getInputProps(fields.hourlyRate!, { type: 'number' }),
									placeholder: '25.00',
									step: '0.01',
									min: '0',
								}}
								errors={fields.hourlyRate?.errors}
							/>
						</div>

						<TextareaField
							labelProps={{ children: 'Description' }}
							textareaProps={{
								...getInputProps(fields.description!, { type: 'text' }),
								placeholder: 'Describe your rental item...',
								rows: 4,
							}}
							errors={fields.description?.errors}
						/>

						{/* Image Upload Section - Only for authenticated users */}
						{user ? (
							<div className="mb-6">
								<label className="mb-2 block text-sm font-medium">Image</label>
								<div className="space-y-4">
									{!hasUploadedImage ? (
										<>
											<div
												{...getRootProps()}
												className={`border-muted-foreground/25 hover:border-primary flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
													isDragActive ? 'border-primary bg-primary/5' : ''
												} ${fields.featuredMediaId?.errors?.length ? 'border-destructive' : ''}`}
											>
												<input {...getDropzoneInputProps()} />
												<Icon
													name="upload"
													className="text-muted-foreground mb-4 size-12"
												/>
												<p className="text-muted-foreground mb-2 text-center text-sm">
													{isDragActive
														? 'Drop your image here'
														: 'Drag & drop an image here'}
												</p>
												<p className="text-muted-foreground mb-2 text-center text-xs">
													JPEG, PNG, or WebP (max 5MB)
												</p>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={open}
													className="mt-2"
												>
													<Icon name="upload" className="mr-2 size-4" />
													Choose File
												</Button>
											</div>

											{files.length > 0 && (
												<div className="space-y-2">
													{files.map((file, index) => (
														<div
															key={index}
															className="bg-muted flex items-center justify-between rounded-lg p-3"
														>
															<div className="flex items-center gap-2">
																<Icon name="file" className="size-4" />
																<span className="text-sm">{file.name}</span>
															</div>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() => {
																	setFiles(files.filter((_, i) => i !== index))
																}}
															>
																<Icon name="cross-1" className="size-4" />
															</Button>
														</div>
													))}
													<Button
														type="button"
														onClick={onUpload}
														disabled={uploading || files.length === 0}
														className="w-full"
													>
														{uploading ? 'Uploading...' : 'Upload Image'}
													</Button>
												</div>
											)}

											{uploadErrors.length > 0 && (
												<div className="text-destructive text-sm">
													{uploadErrors.map((error, index) => (
														<p key={index}>{error.message}</p>
													))}
												</div>
											)}
										</>
									) : (
										<div className="bg-muted rounded-lg p-4">
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-2">
													<Icon
														name="check-circled"
														className="size-5 text-green-600"
													/>
													<span className="text-sm font-medium">
														Image uploaded successfully
													</span>
												</div>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														setUploadedMediaId('')
														setUploadedMedia([])
														setFiles([])
													}}
												>
													<Icon name="cross-1" className="size-4" />
												</Button>
											</div>
										</div>
									)}
								</div>
								<ErrorList
									id={`${fields.featuredMediaId?.id}-error`}
									errors={fields.featuredMediaId?.errors}
								/>
							</div>
						) : (
							<div className="bg-muted mb-6 rounded-lg border p-6">
								<div className="flex items-start gap-3">
									<Icon
										name="info-circled"
										className="mt-1 size-5 text-blue-600"
									/>
									<div className="flex-1">
										<h4 className="mb-1 font-medium">
											Sign up to upload an image
										</h4>
										<p className="text-muted-foreground mb-3 text-sm">
											Create an account to upload images for your listing. Your
											information will be saved and you can complete your
											submission after signing up.
										</p>
										<div className="flex gap-2">
											<Button size="sm" asChild>
												<Link to="/signup">Sign Up</Link>
											</Button>
											<Button size="sm" variant="outline" asChild>
												<Link to="/login">Log In</Link>
											</Button>
										</div>
									</div>
								</div>
							</div>
						)}

						{!user ? (
							<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
								<Field
									labelProps={{ children: 'Your Name' }}
									inputProps={{
										...getInputProps(fields.contactName, { type: 'text' }),
										placeholder: 'Jane Doe',
									}}
									errors={fields.contactName?.errors}
								/>
								<Field
									labelProps={{ children: 'Your Email' }}
									inputProps={{
										...getInputProps(fields.contactEmail, { type: 'email' }),
										placeholder: 'you@example.com',
									}}
									errors={fields.contactEmail?.errors}
								/>
							</div>
						) : null}

						<StatusButton
							type="submit"
							className="w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							disabled={isPending}
						>
							Submit Listing
						</StatusButton>
					</Form>
				</CardContent>
			</Card>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
